import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Tag } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { Field, PlainSelect } from "./Common";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";

const BLANK = { code: "", label: "", type: "percent", value: 10, expires_on: "", max_uses: 0, once_per_patient: true, email_domain: "", active: true };
const toForm = (c) => ({ code: c.code, label: c.label, type: c.percent ? "percent" : "amount", value: c.percent || c.amount_off, expires_on: c.expires_at ? c.expires_at.slice(0, 10) : "",
  max_uses: c.max_uses, once_per_patient: c.once_per_patient, email_domain: c.email_domain, active: c.active });

function CodeEditor({ c, onDone }) {
  const [f, setF] = useState(c ? toForm(c) : BLANK);
  const id = c?.id || "new";
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = () => {
    const body = { ...f, value: Number(f.value), max_uses: Number(f.max_uses) || 0 };
    (c ? api.put(`/discount-codes/${c.id}`, body) : api.post("/discount-codes", body))
      .then(() => { toast.success("Discount code saved"); if (!c) setF(BLANK); onDone(); }).catch((e) => toast.error(errMsg(e)));
  };
  const del = () => window.confirm("Delete this code?") && api.delete(`/discount-codes/${c.id}`).then(onDone);
  return (
    <div className="rounded-[var(--radius)] border bg-card p-4 space-y-3" data-testid={`dcode-${id}`}>
      <div className="flex flex-wrap items-center gap-2">
        {c ? <b className="tracking-wider" data-testid={`dcode-code-${id}`}>{c.code}</b> : <Input className="w-44 uppercase" data-testid="dcode-code-new" placeholder="Code (blank = auto)" value={f.code} onChange={set("code")} />}
        <Input className="flex-1 min-w-[200px]" data-testid={`dcode-label-${id}`} placeholder="What is it for? e.g. Newsletter subscribers, Acme Ltd staff" value={f.label} onChange={set("label")} />
        {c && <span className="text-xs text-muted-foreground" data-testid={`dcode-uses-${id}`}>Used {c.uses}×{c.max_uses ? ` of ${c.max_uses}` : ""}</span>}
        <label className="flex items-center gap-2 text-sm"><Switch data-testid={`dcode-active-${id}`} checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} />{f.active ? "Active" : "Paused"}</label>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        <Field label="Type"><PlainSelect data-testid={`dcode-type-${id}`} value={f.type} onChange={set("type")}><option value="percent">% off</option><option value="amount">Fixed amount off</option></PlainSelect></Field>
        <Field label={f.type === "percent" ? "Percent" : "Amount"}><Input type="number" min={1} data-testid={`dcode-value-${id}`} value={f.value} onChange={set("value")} /></Field>
        <Field label="Expires (optional)"><Input type="date" data-testid={`dcode-expires-${id}`} value={f.expires_on} onChange={set("expires_on")} /></Field>
        <Field label="Max uses (0 = no limit)"><Input type="number" min={0} data-testid={`dcode-max-${id}`} value={f.max_uses} onChange={set("max_uses")} /></Field>
        <Field label="Only emails ending @"><Input data-testid={`dcode-domain-${id}`} placeholder="company.com" value={f.email_domain} onChange={set("email_domain")} /></Field>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-sm"><Switch data-testid={`dcode-once-${id}`} checked={f.once_per_patient} onCheckedChange={(v) => setF({ ...f, once_per_patient: v })} />Once per patient</label>
        <Button size="sm" className="rounded-full ml-auto" data-testid={`dcode-save-${id}`} disabled={!(Number(f.value) > 0)} onClick={save}>{c ? "Save" : "Create code"}</Button>
        {c && <Button size="sm" variant="ghost" data-testid={`dcode-delete-${id}`} onClick={del}>Delete</Button>}
      </div>
    </div>
  );
}

export function DiscountCodes() {
  const [list, setList] = useState([]);
  const load = () => api.get("/discount-codes").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  return (
    <div className="space-y-3" data-testid="discount-codes">
      <h3 className="text-lg flex items-center gap-2"><Tag className="h-5 w-5 text-primary" />Discount codes</h3>
      <p className="text-sm text-muted-foreground">Give these to newsletter subscribers, partner companies or anyone you like. Patients can use them when booking online or when paying at the clinic.</p>
      <CodeEditor onDone={load} />
      {list.map((c) => <CodeEditor key={c.id} c={c} onDone={load} />)}
    </div>
  );
}
